// ABOUTME: TypeScript types for the Order Intake Lead Time configuration feature.
// ABOUTME: Covers API response/request shapes, form values, override tracking, and enum types.

import {MetaResponse, OverrideValue} from '.'
import {DAY_CODE_TO_ID, DAY_ID_TO_CODE, DAYS, DayCode, DayIdentifier} from './deliveryWindow'

// --- Enums ---

export type ReferenceEvent =
  | 'beforePrePlanning'
  | 'beforeRequestedDeliveryTime'
  | 'afterFirstTruckLoaded'
  | 'afterMinQuantityReached'
  | 'afterOrderCompleted'
  | 'afterOrderTicketed'

export type CalculationMethod =
  | 'basePlusAllModifiers'
  | 'largestOnly'

// --- Shared UoM reference ---

export interface UomRef {
  id: string
  name: string
  displaySymbol: string
}

// --- API response types ---

export interface FarDistanceThresholdValue {
  threshold: number
  uom: UomRef
}

export interface LeadTimeRuleResponse {
  applicableDays: string[]
  baseLeadTime: number
  baseLeadTimeUom: UomRef
  referenceEvent: ReferenceEvent
  farDistanceModifier: number
  farDistanceModifierUom: UomRef
  calculationMethod: CalculationMethod
}

export interface NonStandardLeadTimeRuleResponse {
  day: string
  baseLeadTime: number
  baseLeadTimeUom: UomRef
  referenceEvent: ReferenceEvent
  farDistanceModifier: number
  farDistanceModifierUom: UomRef
  calculationMethod: CalculationMethod
}

export interface LeadTimesValue {
  standard: LeadTimeRuleResponse
  nonStandard: NonStandardLeadTimeRuleResponse[]
}

export interface OrderIntakeLeadTimeApiResponse {
  scopeId: string
  id: string
  farDistanceThreshold: OverrideValue<FarDistanceThresholdValue | null>
  leadTimes: OverrideValue<LeadTimesValue | null>
  _meta: MetaResponse
}

// --- API request types ---

export interface FarDistanceThresholdRequestValue {
  threshold: number
  uomId: string
}

export interface LeadTimeRuleRequest {
  applicableDays: string[]
  baseLeadTime: number
  baseLeadTimeUomId: string
  referenceEvent: ReferenceEvent
  farDistanceModifier: number
  farDistanceModifierUomId: string
  calculationMethod: CalculationMethod
}

export interface NonStandardLeadTimeRuleRequest {
  day: string
  baseLeadTime: number
  baseLeadTimeUomId: string
  referenceEvent: ReferenceEvent
  farDistanceModifier: number
  farDistanceModifierUomId: string
  calculationMethod: CalculationMethod
}

export interface LeadTimesRequestValue {
  standard: LeadTimeRuleRequest
  nonStandard: NonStandardLeadTimeRuleRequest[]
}

export interface PutOrderIntakeLeadTimeRequest {
  farDistanceThreshold: OverrideValue<FarDistanceThresholdRequestValue | null>
  leadTimes: OverrideValue<LeadTimesRequestValue | null>
}

// --- Override tracking ---

export interface OrderIntakeLeadTimeOverrides {
  farDistanceThreshold: boolean
  leadTimes: boolean
}

// --- Form-ready types ---

export interface OrderIntakeLeadTimeMeta {
  scopeId: string
  isInherited: boolean
  resolvedFrom: string | null
  createdAt: string
  updatedAt: string
  createdBy: string
  updatedBy: string
}

export interface StandardLeadTimeFormValues {
  applicableDays: string[]
  baseLeadTime: number
  baseLeadTimeUomId: string
  baseLeadTimeUomDisplay: string
  referenceEvent: ReferenceEvent
  farDistanceModifier: number
  farDistanceModifierUomId: string
  farDistanceModifierUomDisplay: string
  calculationMethod: CalculationMethod
}

export interface NonStandardLeadTimeFormValues {
  day: string
  baseLeadTime: number
  baseLeadTimeUomId: string
  baseLeadTimeUomDisplay: string
  referenceEvent: ReferenceEvent
  farDistanceModifier: number
  farDistanceModifierUomId: string
  farDistanceModifierUomDisplay: string
  calculationMethod: CalculationMethod
}

export interface OrderIntakeLeadTimeConfig {
  id: string | undefined
  farDistanceThreshold: number
  farDistanceThresholdUomId: string | undefined
  farDistanceThresholdUomDisplay: string
  standard: StandardLeadTimeFormValues | null
  nonStandard: NonStandardLeadTimeFormValues[]
  distanceUomOptions: {value: string; label: string}[]
  overrides: OrderIntakeLeadTimeOverrides
  parentValues: OrderIntakeLeadTimeFormValues
  _meta: OrderIntakeLeadTimeMeta
}

export interface OrderIntakeLeadTimeFormValues {
  farDistanceThreshold: number
  farDistanceThresholdUomId: string
  farDistanceThresholdUomDisplay: string
  standard: StandardLeadTimeFormValues | null
  nonStandard: NonStandardLeadTimeFormValues[]
}

// --- Constants ---

export const TIME_UOM_OPTIONS = [
  {value: 'businessMinute', label: 'business mins'},
  {value: 'businessHour', label: 'business hours'},
  {value: 'businessDay', label: 'business days'},
  {value: 'min', label: 'minutes'},
  {value: 'h', label: 'hours'},
  {value: 'd', label: 'days'},
]

export const REFERENCE_EVENTS: ReferenceEvent[] = [
  'beforePrePlanning',
  'beforeRequestedDeliveryTime',
  'afterFirstTruckLoaded',
  'afterMinQuantityReached',
  'afterOrderCompleted',
  'afterOrderTicketed',
]

export const CALCULATION_METHODS: CalculationMethod[] = [
  'basePlusAllModifiers',
  'largestOnly',
]

// --- Day mapping helpers ---

/** Map a backend day string (3-letter code or lowercase full name) to DayIdentifier */
export const toDayIdentifier = (day: string): DayIdentifier | undefined => {
  const upper = day.toUpperCase()
  if (upper in DAY_CODE_TO_ID) return DAY_CODE_TO_ID[upper as DayCode]
  const lower = day.toLowerCase() as DayIdentifier
  if (DAYS.includes(lower)) return lower
  return undefined
}

/** Map DayIdentifier back to the format used in the original config */
export const fromDayIdentifier = (id: DayIdentifier, usesCodes: boolean): string =>
  usesCodes ? DAY_ID_TO_CODE[id] : id

/** Maps both short (mon) and long (monday) day identifiers to i18n keys.
 * Supports both formats since the backend may return either. */
export const DAY_FULL_NAME_KEYS: Record<string, string> = {
  mon: 'countryConfigurations.days.mondayFull',
  tue: 'countryConfigurations.days.tuesdayFull',
  wed: 'countryConfigurations.days.wednesdayFull',
  thu: 'countryConfigurations.days.thursdayFull',
  fri: 'countryConfigurations.days.fridayFull',
  sat: 'countryConfigurations.days.saturdayFull',
  sun: 'countryConfigurations.days.sundayFull',
  hol: 'countryConfigurations.days.holidayFull',
  monday: 'countryConfigurations.days.mondayFull',
  tuesday: 'countryConfigurations.days.tuesdayFull',
  wednesday: 'countryConfigurations.days.wednesdayFull',
  thursday: 'countryConfigurations.days.thursdayFull',
  friday: 'countryConfigurations.days.fridayFull',
  saturday: 'countryConfigurations.days.saturdayFull',
  sunday: 'countryConfigurations.days.sundayFull',
  holiday: 'countryConfigurations.days.holidayFull',
}

// --- Form value conversion ---

export const toFormValues = (cfg: OrderIntakeLeadTimeConfig): OrderIntakeLeadTimeFormValues => ({
  farDistanceThreshold: cfg.farDistanceThreshold,
  farDistanceThresholdUomId: cfg.farDistanceThresholdUomId ?? '',
  farDistanceThresholdUomDisplay: cfg.farDistanceThresholdUomDisplay,
  standard: cfg.standard,
  nonStandard: cfg.nonStandard,
})
